import { useCallback, useState } from "react";
import { TbBuilding, TbCopy, TbUser, TbWallet } from "react-icons/tb";
import intl from "react-intl-universal";
import {
  formatIbAccountHomeAgencyAccountDisplay,
  formatIbAccountHomeBankDetailsCopyText,
  formatIbAccountHomeBankIspbLine,
  formatIbAccountHomeDocumentLine,
  formatIbAccountHomeTypeLabel,
  resolveIbAccountDisplayType,
} from "../../../Services/operatingAccountsApi";

const TypeIcon = ({ type, className }) => {
  if (type === "PERSON") return <TbUser className={className} aria-hidden />;
  if (type === "VIRTUAL") return <TbWallet className={className} aria-hidden />;
  return <TbBuilding className={className} aria-hidden />;
};

/**
 * Active IB account summary for Dashboard greeting (VIRTUAL included):
 * gold type → dashed card (CNPJ / Banco-ISPB / AG·CC) with card-level copy.
 * Entity name is shown only in ScreenGreeting, not duplicated here.
 */
export const HomeActiveAccountBlock = ({ account = null }) => {
  const [copied, setCopied] = useState(false);

  const documentLine =
    account?.homeDocumentLine
    || formatIbAccountHomeDocumentLine(account)
    || "";
  const bankIspbLine =
    account?.homeBankIspbLine
    || formatIbAccountHomeBankIspbLine(account)
    || "";
  const agencyAccountLine =
    account?.homeAgencyAccountLine
    || formatIbAccountHomeAgencyAccountDisplay(account)
    || "";
  const bankDetailsCopyText =
    account?.homeBankDetailsCopyText
    || formatIbAccountHomeBankDetailsCopyText(account)
    || "";

  const handleCopyBankDetails = useCallback(async () => {
    if (!bankDetailsCopyText) return;
    try {
      await navigator.clipboard.writeText(bankDetailsCopyText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [bankDetailsCopyText]);

  if (!account) return null;

  const displayType = resolveIbAccountDisplayType(account);
  const typeLabel =
    account.homeTypeLabel
    || formatIbAccountHomeTypeLabel(account)
    || "";

  const hasDetailsCard = Boolean(documentLine || bankIspbLine || agencyAccountLine);

  if (!typeLabel && !hasDetailsCard) {
    return null;
  }

  const copyLabel = copied
    ? intl.get("carteira.gate.copyBankDetailsSuccess")
    : intl.get("carteira.gate.copyBankDetails");

  return (
    <div className="mt-3 min-w-0 w-full" data-testid="home-active-account-block">
      {typeLabel ? (
        <p className="m-0 flex min-w-0 items-center gap-1.5 text-[11px] font-semibold tracking-wide text-brand-gold dark:text-brand-gold-mid">
          <TypeIcon
            type={displayType}
            className="h-3.5 w-3.5 shrink-0 text-brand-gold dark:text-brand-gold-mid"
          />
          <span className="min-w-0 truncate">{typeLabel}</span>
        </p>
      ) : null}

      {hasDetailsCard ? (
        <div className="relative mt-1.5 inline-flex w-fit max-w-full flex-col rounded-md border border-dashed border-gray-300 py-1.5 pl-2.5 pr-8 dark:border-gray-600">
          {bankDetailsCopyText ? (
            <button
              type="button"
              className="absolute right-1.5 top-1.5 inline-flex shrink-0 items-center justify-center rounded p-0.5 text-brand-gold hover:bg-gray-100 hover:opacity-80 dark:text-brand-gold-mid dark:hover:bg-[#262626]"
              onClick={handleCopyBankDetails}
              title={copyLabel}
              aria-label={copyLabel}
            >
              <TbCopy className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}

          {documentLine ? (
            <p className="m-0 min-w-0 break-words font-mono text-xs text-gray-600 dark:text-gray-300 sm:text-sm">
              {documentLine}
            </p>
          ) : null}

          {bankIspbLine ? (
            <p className="m-0 mt-0.5 min-w-0 break-words font-mono text-xs text-gray-600 dark:text-gray-300 sm:text-sm">
              {bankIspbLine}
            </p>
          ) : null}

          {agencyAccountLine ? (
            <p className="m-0 mt-0.5 min-w-0 break-words font-mono text-xs text-gray-600 dark:text-gray-300 sm:text-sm">
              {agencyAccountLine}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

HomeActiveAccountBlock.propTypes = {};
